import { UserInfo } from '@mango-engineering/maknet-client-sdk/lib/shop/makroUser/types';
import { CollectionType } from '@mango-engineering/maknet-client-sdk/lib/shop/product/enum/collectionType.enum';
import { ProductModel } from '@mango-engineering/maknet-client-sdk/lib/shop/product/model/product.model';
import {
  ProductAvailability,
  ProductStatusType,
  getProductStatus as getProductAvailabilityWithFlag,
} from '@mango-engineering/maknet-client-sdk/lib/shop/product/util/statusUtils';
import { ShoppingDataLayerProduct } from '@makro-shopping/use-product-card-logic/dist/types/product.type';
import { config } from '../config/env';
import { IAvailableLocaleTypes, normalizeLocale } from '../types/locale.types';
import { Product, ProductSearchResult, ProductUnavailableReasonCodes, SlabPriceTier } from '../types/product.types';
import { isFeatureEnabled } from './common.util';
import { FALLBACK_APP_VERSION, PRODUCT_ID_PREFIX, PRODUCT_TYPE_3P } from './constants';
import i18n from './i18n';

type ShoppingDataLayerProductWithStoreCode = ShoppingDataLayerProduct & {
  // Product Service can still provide this legacy field for search products,
  // even though the product-card package no longer includes it in its type.
  storeCode?: string;
  plpVideoUrl?: string | null;
};

type ProductWithVideoUrl = ShoppingDataLayerProductWithStoreCode & {
  videoUrl?: string;
};

export const getProductVideoUrl = (
  product: Product | ProductSearchResult | ShoppingDataLayerProductWithStoreCode,
): string | null | undefined => {
  if ('plpVideoUrl' in product && product.plpVideoUrl != null) {
    return product.plpVideoUrl;
  }

  return 'videoUrl' in product ? product.videoUrl : undefined;
};

export const getProductIdWithoutPrefix = (id: string): string => {
  return id?.includes(PRODUCT_ID_PREFIX)
    ? id?.split(PRODUCT_ID_PREFIX).pop() || ''
    : id;
};

export const getIdFromString = (inputString: string) => {
  const parts = inputString.split('/');
  return parts[parts?.length - 1];
};

export const getWholeNumberOrTwoDecimal = (value: number): number => {
  return Math.round(value * 100) / 100;
};

export const findNearestValueByMOQ = (moq: number, value: number): number => {
  const remainder = value % moq;
  if (remainder === 0) return value;

  const lowerBound = Math.floor(value / moq) * moq;
  const upperBound = lowerBound + moq;

  return value - lowerBound < upperBound - value ? lowerBound : upperBound;
};

export const getProductAvailability = (
  product: Product,
  storeCodes: string[],
  userInfo?: UserInfo,
) => {
  const status =
    product.unavailableReason?.code ===
    ProductUnavailableReasonCodes.UNAVAILABLE_IN_RM3
      ? ProductAvailability.unavailable
      : getProductAvailabilityWithFlag(
          product,
          storeCodes,
          config.ENABLE_3P_OWNFLEET,
          userInfo,
        );

  return {
    status,
    isAvailable: status === ProductAvailability.available,
    isOutOfStock: status === ProductAvailability.outOfStock,
    isNotDeliverable: status === ProductAvailability.notDeliverable,
    isUnavailable: status === ProductAvailability.unavailable,
    isDisabled: status !== ProductAvailability.available,
  };
};

export const defaultDeliveryDescription: any = {
  singleLine: '',
  twoLine: { line1: '', line2: '' },
  singleLineV2: '',
  popup: '',
};

const CHINESE_DELIVERY_TEXT: Record<string, { 'zh-Hans': string; 'zh-Hant': string }> = {
  NEXT_DAY: { 'zh-Hans': '次日', 'zh-Hant': '次日' },
  SAME_DAY: { 'zh-Hans': '当日', 'zh-Hant': '當日' },
  EXPRESS_DELIVERY: { 'zh-Hans': '快速配送', 'zh-Hant': '快速配送' },
  PRE_ORDER: { 'zh-Hans': '预购', 'zh-Hant': '預購' },
  BULKY1P: { 'zh-Hans': '标准配送', 'zh-Hant': '標準配送' },
  DROPSHIP: { 'zh-Hans': '标准配送', 'zh-Hant': '標準配送' },
  ONE_PX: { 'zh-Hans': '次日', 'zh-Hant': '次日' },
};

export const getIs2PEnabledForCountry = (countryCode?: string, appVersion?: string): boolean => {
  const minVersion = config.TWO_P_MIN_APP_VERSION?.[countryCode?.toUpperCase() || ''];
  const currentVersion =
    appVersion ||
    (typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('appVersion') : null) ||
    FALLBACK_APP_VERSION;

  return Boolean(minVersion && isFeatureEnabled(minVersion, currentVersion));
};

// Get delivery type config from Doppler or use default fallback
// This allows updating delivery descriptions without code changes
const getDeliveryTypeConfig = (): Record<
  string,
  {
    text: { en: string; th: string; km: string; mm: string; 'zh-Hans': string; 'zh-Hant': string };
    icon: string;
    color: string;
  }
> => {
  try {
    const configJson = config.DELIVERY_DESCRIPTION_FALLBACK_CONFIG;
    if (configJson) {
      return JSON.parse(configJson);
    }
  } catch (error) {
    console.error('Failed to parse DELIVERY_DESCRIPTION_FALLBACK_CONFIG, using default:', error);
  }

  // Default fallback configuration
  return {
    NEXT_DAY: {
      text: {
        en: 'Next Day',
        th: 'ส่งวันถัดไป',
        km: 'ថ្ងៃបន្ទាប់',
        mm: 'နောက်နေ့',
        'zh-Hans': '次日',
        'zh-Hant': '次日',
      },
      icon: 'nextDayTruckIconSmall',
      color: 'cyanText',
    },
    SAME_DAY: {
      text: {
        en: 'Today',
        th: 'สั่งเช้า ส่งเย็น',
        km: 'ថ្ងៃនេះ',
        mm: 'ယနေ့',
        'zh-Hans': '当日',
        'zh-Hant': '當日',
      },
      icon: 'sameDayTruckIconSmallV2',
      color: 'redText',
    },
    EXPRESS_DELIVERY: {
      text: {
        en: 'Express',
        th: 'ด่วน',
        km: 'រហ័ស',
        mm: '၃ နာရီအတွင်း',
        'zh-Hans': '快速配送',
        'zh-Hant': '快速配送',
      },
      icon: 'expressDeliveryIconSmall',
      color: 'gradientText',
    },
    PRE_ORDER: {
      text: {
        en: 'Pre-Order',
        th: 'พรีออเดอร์',
        km: 'ការកម្មង់មុន',
        mm: 'ကြိုမှာယူခြင်း',
        'zh-Hans': '预购',
        'zh-Hant': '預購',
      },
      icon: 'preOrderTruckIconSmall',
      color: 'cyanText',
    },
    BULKY1P: {
      text: {
        en: 'Standard',
        th: 'มาตรฐาน',
        km: 'ស្តង់ដារ',
        mm: 'ပုံမှန်',
        'zh-Hans': '标准配送',
        'zh-Hant': '標準配送',
      },
      icon: 'bulkyTruckIconSmall',
      color: 'cyanText',
    },
    DROPSHIP: {
      text: {
        en: 'Standard',
        th: 'มาตรฐาน',
        km: 'ស្តង់ដារ',
        mm: 'ပုံမှန်',
        'zh-Hans': '标准配送',
        'zh-Hant': '標準配送',
      },
      icon: 'bulkyTruckIconSmall',
      color: 'cyanText',
    },
    ONE_PX: {
      text: {
        en: 'Next Day',
        th: 'ส่งวันถัดไป',
        km: 'ថ្ងៃបន្ទាប់',
        mm: 'နောက်နေ့',
        'zh-Hans': '次日',
        'zh-Hant': '次日',
      },
      icon: 'nextDayTruckIconSmall',
      color: 'cyanText',
    },
  };
};

/**
 * Get delivery markup text based on deliveryType
 * Returns markup format that deliveryInfoParser can parse
 */
export const getDeliveryTextFromType = (
  deliveryType: string | undefined,
  locale: string = 'en',
): string => {
  if (!deliveryType) return '';

  const deliveryConfig = getDeliveryTypeConfig();
  const typeConfig = deliveryConfig[deliveryType];
  if (!typeConfig) return '';

  const normalizedLocale = normalizeLocale(locale);
  const lang = normalizedLocale === IAvailableLocaleTypes.KH ? 'km' : normalizedLocale;
  const chineseFallback =
    normalizedLocale === IAvailableLocaleTypes.ZHHANS || normalizedLocale === IAvailableLocaleTypes.ZHHANT
      ? CHINESE_DELIVERY_TEXT[deliveryType]?.[normalizedLocale]
      : undefined;
  const text = typeConfig.text[lang as keyof typeof typeConfig.text] || chineseFallback || typeConfig.text.en;

  return `<${typeConfig.color}>${text}</${typeConfig.color}> <${typeConfig.icon} />`;
};

/**
 * Applies fallback delivery description when backend returns raw format like "1-1 Days"
 *
 * Country-specific rules (configurable via DELIVERY_DESCRIPTION_FALLBACK_COUNTRY_CONFIG):
 * - enabled: Whether to apply fallback for this country
 * - preferFallback: If true, use fallback text; if false, keep original text
 */
export const applyDeliveryDescriptionFallback = (
  deliveryDescription: any | undefined,
  deliveryType: string | undefined,
  countryCode: string,
  locale: string,
): any => {
  if (!deliveryDescription) return defaultDeliveryDescription;

  // Check country-specific config from Doppler
  const countryConfig = config.DELIVERY_DESCRIPTION_FALLBACK_COUNTRY_CONFIG;
  const countrySettings = countryConfig?.[countryCode] || { enabled: true, preferFallback: true };

  // If fallback is disabled for this country, return as-is
  if (!countrySettings.enabled) {
    return deliveryDescription;
  }

  // Check if delivery description has the raw "X-X Days" or "X-X วัน" pattern
  const daysPattern = /\d+-\d+\s*(Days?|วัน|ថ្ងៃ|ရက់)/i;
  const hasDaysPattern =
    (deliveryDescription.singleLine && daysPattern.test(deliveryDescription.singleLine)) ||
    (deliveryDescription.singleLineV2 && daysPattern.test(deliveryDescription.singleLineV2));

  if (!hasDaysPattern) return deliveryDescription;

  // If preferFallback is false, keep the original text (e.g., Myanmar keeps "1-4 Days")
  if (!countrySettings.preferFallback) {
    return deliveryDescription;
  }

  // Use deliveryType to generate proper fallback text
  if (!deliveryType) return deliveryDescription;

  const fallbackText = getDeliveryTextFromType(deliveryType, locale);
  return {
    ...deliveryDescription,
    singleLine: fallbackText || deliveryDescription.singleLine,
    singleLineV2: fallbackText || deliveryDescription.singleLineV2,
  };
};

/**
 * Format product model and apply delivery description fallback
 * This function is called by ProductService when fetching products
 */
export const formatProduct = (
  input: ProductModel,
  countryCode?: string,
  locale?: string,
  appVersion?: string,
): Product => {
  // Get locale from i18n if not provided
  const currentLocale = locale || i18n.language || 'en';
  
  // Get countryCode from URL params if not provided
  let currentCountryCode = countryCode;
  if (!currentCountryCode) {
    const searchParams = new URLSearchParams(window.location.search);
    currentCountryCode = searchParams.get('countryCode') || '';
  }
  const is2PEnabled = getIs2PEnabledForCountry(currentCountryCode, appVersion);

  // Extract deliveryType from deliveryInfo array (first item)
  const deliveryType = input.deliveryInfo?.[0]?.deliveryType;

  // Get deliveryDescription from top-level or from deliveryInfo as fallback
  const deliveryDescriptionSource = input.deliveryDescription || input.deliveryInfo?.[0]?.deliveryDescription;

  // Apply delivery description fallback for countries with raw delivery format
  const processedDeliveryDescription = applyDeliveryDescriptionFallback(
    //  input.deliveryDescription,
    deliveryDescriptionSource,
    deliveryType,
    currentCountryCode,
    currentLocale,
  );

  const product: Product = {
    ...input,
    id: input.id || '',
    sku: input.sku || '',
    description: input.description || '',
    imageUrls: input.imageUrls || [],
    size: input.size ?? '',
    priceUnit: input.priceUnit || '',
    totalInventory: input.totalInventory || 0,
    displayPrice: input.displayPrice || 0,
    originPrice: input.originPrice || 0,
    status: input.status || ProductStatusType.ACTIVE,
    seller: input.seller || '1P - Makro',
    isBulky: !!input.isBulky,
    is1PX: !!input.is1PX,
    isPreOrder: !!input.isPreOrder,
    deliveryIn24Hrs: !!input.deliveryIn24Hrs,
    isWeightedFresh: !!input.isWeightedFresh,
    isAlcohol: !!input.isAlcohol,
    isUltraFresh: !!input.isUltraFresh,
    sellInPiece: !!input.sellInPiece,

    storeCode: '',
    sellerDetails: {
      shopId: input.sellerDetails?.shopId || 0,
      shopName: input.sellerDetails?.shopName || '',
      marketplaceType: input.sellerDetails?.marketplaceType || null,
      fulfilledByPlatform: input.sellerDetails?.fulfilledByPlatform,
      pro_details: {
        VAT_number: 0,
        corporate_name: '',
        identification_number: '',
        tax_identification_number: '',
      },
    },
    measurements: input.measurements || {},
    shippingInfo: input.shippingInfo?.map((el) => {
      return {
        ...el,
        deliveryTimeEarliestDate: el.deliveryTimeEarliestDate
          ? new Date(el.deliveryTimeEarliestDate.toString())
          : undefined,
        deliveryTimeLatestDate: el.deliveryTimeLatestDate
          ? new Date(el.deliveryTimeLatestDate.toString())
          : undefined,
      };
    }),
    variants:
      input.variants && input.variants?.length > 0
        ? input.variants?.map((el) => {
            return {
              ...el,
              price: el.price || 0,
              sku: el.sku || '',
              outsideOfDeliveryZone: el.outsideOfDeliveryZone || false,
              is2P: is2PEnabled && (el.is2P ?? false),
              storeCode: el.storeCode || '',
              compareAtPrice: el.compareAtPrice || '',
            };
          })
        : [
            {
              id: '',
              sku: '',
              price: 0,
              outsideOfDeliveryZone: false,
              is2P: false,
              storeCode: '',
              compareAtPrice: '',
            },
          ],

    storageInformation: input.storageInformation || '',
    expiryDays: input.expiryDays,
    collections: input.collections,
    deliveryDescription: processedDeliveryDescription,
    totalPercentageSoldText: input.totalPercentageSoldText || '',
    totalPercentageSoldNumber: input.totalPercentageSoldNumber || 0,
    variantId: '',
  };
  return product;
};

export enum ComplexPromotionType {
  MULTI_BUY = 'MULTI_BUY',
  MIX_MATCH = 'MIX_MATCH',
  SELLER_PROMO = 'SELLER_PROMO',
}

export const DEFAULT = 'DEFAULT';
export const PROPOINT_COLLECTION = 'PROPOINT_PROMOTION';

export enum ComplexPromotionCondition {
  AND = 'AND',
  ALL = 'ALL',
}

export const TRANSLATION_KEYS = {
  PROPOINT: {
    TAG: 'loyalty.proPointPromotions.complexPromotion.proPoint',
    DESCRIPTION: 'loyalty.proPointPromotions.complexPromotion.subHeader',
  },
  [ComplexPromotionType.MULTI_BUY]: {
    TAG: 'loyalty.proPointPromotions.complexPromotion.multiBuy',
    DESCRIPTION: 'loyalty.proPointPromotions.complexPromotion.multiBuyHeader',
  },
  [ComplexPromotionType.MIX_MATCH]: {
    TAG: 'loyalty.proPointPromotions.complexPromotion.comboSet',
    DESCRIPTION: '',
  },
};

export const PROMOTION_COLORS = {
  PROPOINT: {
    BACKGROUND: '#FFF7D4',
    TEXT: '#835111',
  },
  DEFAULT: {
    BACKGROUND: '#EADEFF',
    TEXT: '#6109FF',
  },
};

interface UtilityParams {
  isWeightedFresh?: boolean;
  moq?: number;
  sellInPiece?: boolean;
}

interface WeightDisplaySettings {
  showKg?: boolean;
  enablePartialQuantity?: boolean;
}

export function getWeightDisplayAndPartialQuantitySettings(params: UtilityParams): WeightDisplaySettings {
  const { isWeightedFresh, moq, sellInPiece } = params;

  const isWeightedFreshCommunicateInKGEnabled = true;

  const showKg = isWeightedFresh && isWeightedFreshCommunicateInKGEnabled;

  const enablePartialQuantity = showKg && moq === 1 && sellInPiece === false;

  return {
    showKg: showKg ?? false,
    enablePartialQuantity: enablePartialQuantity ?? false,
  };
}

export const getDiscountDetailFromSlabTiers = (tiers: SlabPriceTier[], productCount: number, displayPrice: number) => {
  let startCount = 0;
  let endCount = 0;
  let priceValue = displayPrice;
  if (tiers?.length) {
    if (productCount === 0) {
      if (tiers.length === 1) {
        startCount = tiers[0].quantity;
        endCount = 0;
        priceValue = tiers[0].priceInVat;
      } else {
        startCount = tiers[0].quantity;
        endCount = tiers[1].quantity - 1;
        priceValue = tiers[0].priceInVat;
      }
    } else {
      tiers.some((tier, index, arr) => {
        if (productCount < tier.quantity && index === 0) {
          startCount = 1;
          endCount = tier.quantity - 1;
          priceValue = displayPrice;
          return true;
        }
        if (productCount < tier.quantity) {
          startCount = arr[index - 1].quantity;
          endCount = tier.quantity - 1;
          priceValue = arr[index - 1].priceInVat;
          return true;
        }
        if (productCount >= tier.quantity && index === arr.length - 1) {
          startCount = tier.quantity;
          endCount = 0;
          priceValue = tier.priceInVat;
          return true;
        }
        return false;
      });
    }
  }

  return {
    priceValue,
    startCount,
    endCount,
  };
};

export const prepareProductForProductCard = (
  product: ShoppingDataLayerProductWithStoreCode,
  shouldHidePromotionImage: boolean,
): ProductWithVideoUrl => {
  const videoUrl = getProductVideoUrl(product);

  return {
    ...product,
    ...(videoUrl && { videoUrl }),
    storeCode: product.storeCode === PRODUCT_TYPE_3P ? '' : product.storeCode,
    size: product.size ?? '',
    ...(shouldHidePromotionImage && {
      collections: (product.collections || []).map((collection) =>
        collection.type === CollectionType.PROMOTION ? { ...collection, imageUrl: '' } : collection,
      ),
    }),
    totalPercentageSoldText: product.totalPercentageSoldText?.replace(
      /^(?::[\w+-]+:|\uD83D\uDD25\uFE0F?)\s*/,
      '',
    ),
  };
};

type SoldPercentageTranslator = (key: string, options?: { percentage: number }) => string;

export const getLocalizedPercentageSoldText = (
  product: Pick<ShoppingDataLayerProduct, 'totalPercentageSoldNumber' | 'totalPercentageSoldText'>,
  translate: SoldPercentageTranslator,
): string | undefined => {
  // Product Service controls whether the progress label should be displayed.
  if (!product.totalPercentageSoldText) return undefined;

  const percentage = Number(product.totalPercentageSoldNumber ?? 0);

  if (percentage === 0) return translate('product.limitedStock');
  if (percentage === 100) return translate('product.progressSoldOut');

  return translate('product.percentSold', { percentage });
};
